import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Code2, Copy, Check, KeyRound } from 'lucide-react';

const BASE_URL = 'https://contentlab-workflow-runner-wbdlab.web.app/api/v1';

const FLOW = `1. GET  /workflows?status=published     → pick a workflow, get its id
2. GET  /workflows/{id}                  → read inputMask (the form fields)
3. POST /uploads  (only to send a file)  → signed URL; PUT raw bytes to it,
                                           then reference it by storagePath
4. POST /workflows/{id}/execute          → { params, files } → { promptId }
5. GET  /executions/{promptId}           → poll every 3–5s until "completed",
                                           then download each output.url`;

// The prompt a user pastes into their AI coding assistant. Fill the two <...> blanks.
const VIBE_PROMPT = `Build me a <LANGUAGE / HOST — e.g. "Python CLI" or "Blender add-on panel">
connector for the "AICS Pro Workflow Runner" REST API. It should let me pick a
published workflow, fill in its inputs, run it, and get the outputs back.

API contract:
- Base URL: ${BASE_URL}
- Auth: send header  Authorization: Bearer <TOOL_KEY>  on every request. My Tool
  Key is aicsp_... — read it from an env var / config file, never hard-code it.
- GET /workflows?status=published -> { "workflows": [ { id, name, description } ] }
- GET /workflows/{id} -> { inputMask: [ { id, label, widget, defaultValue,
  required, visible, options?, min?, max?, step? } ] }. id is "<nodeId>.<inputName>".
  widget is one of: text, textarea, number, slider, toggle, seed, select,
  file-image, file-video, file-audio. Render only fields where visible is true,
  sorted by order.
- To send a file input: POST /uploads with JSON { fileName, contentType } ->
  { uploadUrl, storagePath }. Then PUT the raw bytes to uploadUrl with header
  Content-Type: <same contentType> and NO Authorization header. Then reference the
  file on execute as { "name": fileName, "storagePath": storagePath }. (Small files
  may instead be sent inline as { "name", "data": "<base64>" }.)
- POST /workflows/{id}/execute with JSON { params: { "<fieldId>": value, ... },
  files: { "<fieldId>": { name, storagePath }, ... } } -> { promptId }. Put
  text/number/toggle/select values in params, file inputs in files.
- GET /executions/{promptId} -> { status, outputs? }. Poll every 3–5 seconds
  (HTTP timeout >= 60s). Terminal statuses: completed (has outputs), error (has
  error), cancelled. Others (queued, running, localizing, lost, unknown): keep polling.
- On completed: outputs is [ { filename, url, type } ]. url is usually a signed URL
  fetchable with no auth. If url is a relative path (starts with /), prefix the host
  and append access_token=<TOOL_KEY> as a query param to fetch it.

Requirements: handle errors (non-2xx bodies are { "error": "..." }), show run
status while polling, save/return each output, keep it dependency-light, and read
the Tool Key from the AICS_TOOL_KEY env var.`;

const PY_EXAMPLE = `import os, time, json, urllib.request

BASE = "${BASE_URL}"
KEY  = os.environ["AICS_TOOL_KEY"]           # aicsp_...

def api(path, method="GET", body=None):
    data = json.dumps(body).encode() if body is not None else None
    req = urllib.request.Request(BASE + path, data=data, method=method,
        headers={"Authorization": "Bearer " + KEY,
                 "Content-Type": "application/json"})
    with urllib.request.urlopen(req, timeout=90) as r:
        return json.load(r)

# 1. pick the first published workflow
wf = api("/workflows?status=published")["workflows"][0]
print("Running:", wf["name"])

# 2. inspect its fields (fill params keyed by these ids)
detail = api("/workflows/" + wf["id"])
for f in sorted((x for x in detail["inputMask"] if x["visible"]),
                key=lambda x: x["order"]):
    print(" field:", f["id"], f["widget"], "default=", f.get("defaultValue"))

# 3 + 4. execute (params keyed by inputMask id; files omitted here)
prompt_id = api("/workflows/%s/execute" % wf["id"], "POST",
               {"params": {}, "files": {}})["promptId"]

# 5. poll, then download outputs
while True:
    st = api("/executions/" + prompt_id)
    print("status:", st["status"])
    if st["status"] in ("completed", "error", "cancelled"):
        break
    time.sleep(4)

for out in st.get("outputs", []):
    url = out["url"]
    if url.startswith("/"):        # relative proxy path -> prefix host + token
        host = BASE.rsplit("/api/", 1)[0]
        sep = "&" if "?" in url else "?"
        url = host + url + sep + "access_token=" + KEY
    urllib.request.urlretrieve(url, out["filename"])
    print("saved", out["filename"])`;

const PY_UPLOAD = `# To send an image input, do this before execute:
tkt = api("/uploads", "POST", {"fileName": "in.png", "contentType": "image/png"})
with open("in.png", "rb") as fh:
    put = urllib.request.Request(tkt["uploadUrl"], data=fh.read(), method="PUT",
        headers={"Content-Type": "image/png"})   # NO auth header on the PUT
    urllib.request.urlopen(put, timeout=90)
# then in execute:
#   files = { "10.image": {"name": "in.png", "storagePath": tkt["storagePath"]} }`;

const GOTCHAS = [
  'Only images, videos, audio, .txt, .psd and .drx may be uploaded (SVG excluded). The extension must be one of these and ' +
    'contentType must match it, or you get a 400 — also for inline { "data" } files and files referenced on execute.',
  'Signed-PUT Content-Type must match what you sent to /uploads, and the PUT carries NO Authorization header (the signature in the URL is the auth).',
  'Upload URLs expire in 15 min; uploaded files auto-delete after 7 days. Upload right before you execute.',
  'storagePath is scoped to you — pass back the exact string /uploads returned; don’t build your own.',
  'Poll slowly (3–5s) with a generous timeout (≥60s) — the poll that first sees completion also localizes outputs and can take several seconds.',
  'Ignore unknown fields in responses; the API adds fields over time.',
  'Errors are always { "error": "..." } with a non-2xx status (a few ComfyUI-dependent calls return HTTP 200 with { "success": false } instead).',
];

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      {label && <div className="text-xs font-medium text-muted-foreground mb-1.5">{label}</div>}
      <Button
        variant="outline"
        size="icon"
        onClick={copy}
        className="absolute right-2 top-2 h-7 w-7 opacity-70 hover:opacity-100"
        aria-label="Copy to clipboard"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <pre className="overflow-x-auto rounded-lg border border-border bg-muted/50 p-4 text-xs leading-relaxed">
        <code className="font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

export default function DevPage() {
  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 overflow-auto">
      <div className="max-w-3xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Code2 className="h-6 w-6" />
            Dev — build your own connector
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            A connector is any app or script that runs an AICS Pro workflow from where you work — a host app, a CLI, a web page, a
            bot — by calling the REST API with your Tool Key. You don’t have to hand-write it: paste the prompt below into your AI
            coding assistant, and use the reference to sanity-check the result.
          </p>
        </header>

        <div
          className={
            'p-4 rounded-lg border border-violet-300/50 dark:border-violet-500/30 ' +
            'bg-violet-50/50 dark:bg-violet-500/10 mb-6 flex items-center gap-3'
          }
        >
          <KeyRound className="h-5 w-5 text-violet-600 dark:text-violet-400 shrink-0" />
          <p className="text-sm text-foreground/80">
            You need a Tool Key. Generate one on the{' '}
            <Link to="/tokens" className="font-medium text-violet-700 dark:text-violet-300 underline underline-offset-2">
              Tool Key
            </Link>{' '}
            page — it looks like <code className="font-mono text-xs">aicsp_…</code> and is shown once. Read it from an env var;
            never hard-code it.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>The flow (5 calls)</CardTitle>
            <CardDescription>
              Base URL <code className="font-mono text-xs">{BASE_URL}</code>. Every request sends the header{' '}
              <code className="font-mono text-xs">Authorization: Bearer aicsp_…</code>. Keys in{' '}
              <code className="font-mono text-xs">params</code>/<code className="font-mono text-xs">files</code> are the{' '}
              <code className="font-mono text-xs">inputMask[].id</code> values (format{' '}
              <code className="font-mono text-xs">&quot;&lt;nodeId&gt;.&lt;inputName&gt;&quot;</code>).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CodeBlock code={FLOW} />
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 1 — Paste this into your AI coding assistant</CardTitle>
            <CardDescription>Fill the two &lt;…&gt; blanks (language/host). It has everything the model needs.</CardDescription>
          </CardHeader>
          <CardContent>
            <CodeBlock code={VIBE_PROMPT} />
            <p className="text-xs text-muted-foreground mt-3">
              Add host-specific asks as needed, e.g. “the default image input should be the current viewport render” or “import
              each output back into the project.”
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2 — Minimal reference (so you can check the result)</CardTitle>
            <CardDescription>
              A complete run in ~35 lines of Python (standard library only). If your generated connector does something
              meaningfully different, question it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CodeBlock code={PY_EXAMPLE} label="Run a workflow and save outputs" />
            <CodeBlock code={PY_UPLOAD} label="Add an image input (signed upload)" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gotchas (the ones that actually bite)</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 list-disc list-inside text-sm text-muted-foreground">
              {GOTCHAS.map((g, i) => (
                <li key={i} className="pl-1">
                  {g}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
